/** What the reading order needs of an object (world box, parent, and its type when known). */
export type Readable = { id: string; parentId: string | null; x: number; y: number; w: number; h: number; type?: string };

/** Containers whose members are reached one by one, right after the container (spec §14: sections). */
const ENTERED = new Set(['section']);

/**
 * Keyboard order of the board (P11 Ruling 6): top-level objects in rows (vertical centres within `rowTolerance` of
 * the row's first object), left to right; a section's children right after it, in the same order. Connectors are
 * skipped (they belong to the objects they join), and a group is a single stop.
 */
export function keyboardOrder(objects: readonly Readable[], rowTolerance = 24): string[] {
  const byParent = new Map<string | null, Readable[]>();
  const ids = new Set(objects.map((o) => o.id));
  for (const o of objects) {
    if (o.type === 'connector') continue;
    const parent = o.parentId !== null && ids.has(o.parentId) ? o.parentId : null;
    byParent.set(parent, [...(byParent.get(parent) ?? []), o]);
  }
  const types = new Map(objects.map((o) => [o.id, o.type]));
  const out: string[] = [];
  const visit = (parent: string | null) => {
    for (const o of rows(byParent.get(parent) ?? [], rowTolerance)) {
      out.push(o.id);
      if (ENTERED.has(types.get(o.id) ?? '')) visit(o.id);
    }
  };
  visit(null);
  return out;
}

function rows(list: readonly Readable[], tolerance: number): Readable[] {
  const cy = (o: Readable) => o.y + o.h / 2;
  const sorted = [...list].sort((a, b) => cy(a) - cy(b));
  const out: Readable[] = [];
  let row: Readable[] = [];
  for (const o of sorted) {
    if (row.length && cy(o) - cy(row[0]!) >= tolerance) {
      out.push(...row.sort((a, b) => a.x - b.x));
      row = [];
    }
    row.push(o);
  }
  out.push(...row.sort((a, b) => a.x - b.x));
  return out;
}
